import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Weidner Via6 DividerImage section.
 *
 * A thin, full-width photo strip used as a visual divider between
 * larger sections. Two instances appear on the Home page in
 * pages.json — one between FloorplansList and AmenitiesGrid (the
 * original divider position), and one below the DividerBG banner.
 * Each instance is wired with a unique `tokenPrefix` prop so the
 * editor can set independent images per slot without one leaking
 * into the other.
 *
 * Intentionally short — ~48 px on desktop, ~32 px on phones — so the
 * band reads as a divider rather than a hero. line-height: 0 on the
 * section eliminates the descender gap that VibesitesImage's inline-
 * replaced rendering would otherwise leave underneath the strip.
 *
 * No design tokens are referenced — no `--vibe-color-*` reads, no
 * theme color fallbacks. The image is the only visual element.
 *
 * Default image: the property's own `photo_gallery[0]`. No hardcoded
 * CDN URL — a Via6 Seattle asset previously shipped to every site on
 * this base theme. Each Home-page instance still gets independent
 * editor state via `tokenPrefix`, so overriding one strip does not
 * leak into the others (the old shared-default bug that led to the
 * hardcoded URL).
 *
 * Shared-component pattern (see reference/shared-component-pattern.md):
 *   - Runtime path is `${prefix}.image` (per-instance state).
 *   - Hidden schema-lint anchor below carries the literal
 *     `dividerimage.image` path so the source-level linter can
 *     statically match the schema key.
 *   - contentSchema key stays at the canonical `dividerimage.image`.
 */

const dividerImageDef = {
  label: 'Divider Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Section divider',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

interface DividerImageProps {
  /** Per-instance content namespace; pages.json supplies one of
   *  `divider-image-top` / `divider-image-bottom`. Falls back to the
   *  canonical schema key segment so the component is usable standalone. */
  tokenPrefix?: string;
}

export function DividerImage({ tokenPrefix }: DividerImageProps = {}) {
  const prefix = tokenPrefix || 'dividerimage';
  const imagePath = `${prefix}.image`;

  const img = useSlotGroup(imagePath, dividerImageDef);

  return (
    <section className="dividerimage-section">
      <EditableSlotGroup groupPath={imagePath} definition={dividerImageDef}>
        <div className="dividerimage-wrap">
          <VibesitesImage
            url={img.image || ''}
            alt={img.alt || 'Section divider'}
            className="dividerimage-img"
          />
        </div>
      </EditableSlotGroup>

      {/* Schema-lint anchor — carries the canonical literal path so the
          source linter can statically match `dividerimage.image`. Never
          rendered visibly; never receives clicks. The visible wrapper
          above is what the editor actually uses. */}
      <EditableSlotGroup
        groupPath="dividerimage.image"
        definition={dividerImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .dividerimage-section {
          width: 100%;
          display: block;
          /* Kill the inline-image descender gap so the band is exactly
             the wrap height — no stray pixel below the strip. */
          line-height: 0;
        }
        .dividerimage-wrap {
          position: relative;
          width: 100%;
          height: 48px;
          overflow: hidden;
        }
        .dividerimage-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        @media (max-width: 767px) {
          .dividerimage-wrap {
            height: 32px;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'DividerImage',
  displayName: 'Divider Image Stripe',
  slotGroups: {
    'dividerimage.image': dividerImageDef,
  },
};

(DividerImage as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default DividerImage;
